import { useState, useEffect, useCallback } from 'react';
import { useEnhancedWebSocket } from './useEnhancedWebSocket';

/**
 * Portfolio-specific WebSocket Hook
 * Handles real-time portfolio updates and position changes
 */
export const usePortfolioWebSocket = (options = {}) => {
  const {
    onPortfolioUpdate,
    onError,
    autoSubscribe = true
  } = options;

  const [portfolioData, setPortfolioData] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const {
    connectionState,
    isConnected,
    subscribeToPortfolio,
    unsubscribeFromPortfolio,
    setEventHandler,
    connect,
    disconnect
  } = useEnhancedWebSocket({
    autoConnect: true,
    reconnectAttempts: 5,
    reconnectInterval: 3000
  });

  /**
   * Handle portfolio updates from WebSocket
   */
  const handlePortfolioUpdate = useCallback((data) => {
    console.log('📊 Portfolio update received:', data);
    
    setPortfolioData(data);
    setLastUpdate(new Date());
    
    // Call external handler if provided
    onPortfolioUpdate?.(data);
  }, [onPortfolioUpdate]);

  /**
   * Handle connection state changes
   */
  const handleConnectionChange = useCallback((state) => {
    console.log('🔌 Portfolio WebSocket connection state:', state);
    
    if (state === 'AUTHENTICATED' && autoSubscribe && !isSubscribed) {
      // Auto-subscribe when connection is established
      setTimeout(() => {
        subscribe();
      }, 1000); // Small delay to ensure authentication is complete
    } else if (state === 'CLOSED') {
      setIsSubscribed(false);
    }
  }, [autoSubscribe, isSubscribed]);

  /**
   * Handle WebSocket errors
   */
  const handleError = useCallback((error) => {
    console.error('❌ Portfolio WebSocket error:', error);
    onError?.(error);
  }, [onError]);

  /**
   * Subscribe to portfolio updates
   */
  const subscribe = useCallback(() => {
    if (isConnected && !isSubscribed) {
      const success = subscribeToPortfolio();
      if (success) {
        setIsSubscribed(true);
        console.log('✅ Subscribed to portfolio updates');
      }
      return success;
    }
    return false;
  }, [isConnected, isSubscribed, subscribeToPortfolio]);

  /**
   * Unsubscribe from portfolio updates
   */
  const unsubscribe = useCallback(() => {
    if (isSubscribed) {
      const success = unsubscribeFromPortfolio();
      if (success) {
        setIsSubscribed(false);
        console.log('❌ Unsubscribed from portfolio updates');
      }
      return success;
    }
    return false;
  }, [isSubscribed, unsubscribeFromPortfolio]);

  /**
   * Manually trigger portfolio refresh
   */
  const refreshPortfolio = useCallback(() => {
    if (isConnected) {
      // Unsubscribe and resubscribe to trigger fresh data
      unsubscribe();
      setTimeout(() => {
        subscribe();
      }, 500);
    }
  }, [isConnected, subscribe, unsubscribe]);

  // Set up event handlers
  useEffect(() => {
    setEventHandler('onPortfolioUpdate', handlePortfolioUpdate);
    setEventHandler('onConnectionChange', handleConnectionChange);
    setEventHandler('onError', handleError);
  }, [setEventHandler, handlePortfolioUpdate, handleConnectionChange, handleError]);

  // Auto-subscribe when connected
  useEffect(() => {
    if (isConnected && autoSubscribe && !isSubscribed) {
      subscribe();
    }
  }, [isConnected, autoSubscribe, isSubscribed, subscribe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isSubscribed) {
        unsubscribe();
      }
    };
  }, [isSubscribed, unsubscribe]);

  return {
    // Portfolio data
    portfolioData,
    lastUpdate,
    
    // Connection state
    isConnected,
    isSubscribed,
    connectionState,
    
    // Control methods
    subscribe,
    unsubscribe,
    refreshPortfolio,
    connect,
    disconnect,
    
    // Utility methods
    hasData: portfolioData !== null,
    isLoading: isConnected && !portfolioData && isSubscribed,
    
    // Portfolio summary shortcuts
    totalValue: portfolioData?.summary?.totalValue || 0,
    totalPnL: portfolioData?.summary?.totalPnL || 0,
    pnL24h: portfolioData?.summary?.pnL24h || 0,
    activeBots: portfolioData?.summary?.activeBots || 0,
    positions: portfolioData?.positions || [],
    performance: portfolioData?.performance || {}
  };
};

export default usePortfolioWebSocket;